import { TenantFormattingService } from '../../../core/services/tenant-formatting.service';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { Router } from '@angular/router';
import { Chart } from 'chart.js/auto';
import { BreakpointObserver } from '@angular/cdk/layout';
import { OrdersService } from '../../../core/services/orders.service';
import { OrderModel } from '../../../core/models/order.model';
import { getOrderProgressLabel, getOrderProgressStyle, OrderProgress } from '../../../core/models/shared/order-progress.enum';
import { LoadingSpinnerComponent } from '../../components/loading-spinner.component/loading-spinner.component';
import { DashboardService } from '../../../core/services/dashboard.service';
import { OrdersByMonthModel } from '../../../core/models/dashboard-summary.model';
import { AppDatePipe } from '../../pipes/app-date.pipe';
import { MatGridListModule } from '@angular/material/grid-list';
import { Subscription } from 'rxjs';
import { NoItemsFoundComponent } from '../../components/no-items-found/no-items-found.component';
import { getOrderStatusLabel, getOrderStatusStyle, OrderStatus } from '../../../core/models/shared/order-status.enum';
import { ErrorAlertComponent } from '../../components/error-alert.component/error-alert.component';
@Component({
  selector: 'app-dashboard',
  imports: [NgStyle, NgTemplateOutlet, LoadingSpinnerComponent, AppDatePipe, MatGridListModule, NoItemsFoundComponent, ErrorAlertComponent],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements AfterViewInit, OnDestroy, OnInit {
  readonly formatting = inject(TenantFormattingService);
  private ordersChartRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('ordersChart')
  set ordersChart(ref: ElementRef<HTMLCanvasElement> | undefined) {
    this.ordersChartRef = ref;
    this.renderOrdersChart();
  }
  private chart?: Chart;
  private breakpointSubscription?: Subscription;
  activeCases = 0;
  recentOrders: OrderModel[] = [];
  loadingOrders = false;
  ordersError = '';
  loadingSummary = false;
  summaryError = '';
  OrderProgress = OrderProgress;
  OrderStatus = OrderStatus;
  readonly getProgressStyle = getOrderProgressStyle;
  readonly getStatusStyle = getOrderStatusStyle;
  ordersByMonth: OrdersByMonthModel[] = [];
  isSmallScreen = false;
  constructor(private ordersService: OrdersService, private dashboardService: DashboardService, private router: Router, private breakpointObserver: BreakpointObserver) { }
  ngOnInit(): void {
    this.breakpointSubscription = this.breakpointObserver
      .observe('(max-width: 767.98px)')
      .subscribe(result => {
        this.isSmallScreen = result.matches;
        setTimeout(() => this.renderOrdersChart());
      });
    this.loadRecentOrders();
    this.loadDashboardSummary();
  }
  ngAfterViewInit(): void {
    this.renderOrdersChart();
  }
  ngOnDestroy(): void {
    this.breakpointSubscription?.unsubscribe();
    this.chart?.destroy();
  }
  openOrder(order: OrderModel): void {
    this.router.navigate(['/orders'], {
      queryParams: {
        search: order.formattedOrderNumber,
        expandId: order.id
      }
    });
  }
  getProgressLabel(progress: OrderProgress): string {
    return getOrderProgressLabel(progress);
  }
  getStatusLabel(status: OrderStatus): string {
    return getOrderStatusLabel(status);
  }
  private loadRecentOrders(): void {
    this.loadingOrders = true;
    this.ordersError = '';
    this.ordersService.getOrders(1, '', [], 4).subscribe({
      next: (data) => {
        this.recentOrders = data.items ?? [];
        this.loadingOrders = false;
      },
      error: (err) => {
        this.ordersError = 'Failed to load recent orders.';
        console.error(err);
        this.loadingOrders = false;
      }
    });
  }
  private loadDashboardSummary(): void {
    this.loadingSummary = true;
    this.summaryError = '';
    this.dashboardService.getSummary().subscribe({
      next: (summary) => {
        this.activeCases = summary.activeCases ?? 0;
        this.ordersByMonth = summary.ordersLast12Months ?? [];
        this.loadingSummary = false;
        queueMicrotask(() => this.renderOrdersChart());
      },
      error: (err) => {
        this.summaryError = 'Failed to load dashboard summary.';
        console.error(err);
        this.loadingSummary = false;
      }
    });
  }
  private renderOrdersChart(): void {
    const canvas = this.ordersChartRef?.nativeElement;
    if (!canvas)
      return;
    if (this.loadingSummary)
      return;
    if (this.summaryError)
      return;
    if (!this.ordersByMonth?.length)
      return;
    this.chart?.destroy();
    const primary = getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim() || '#6495ed';
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const labels = this.ordersByMonth.map(item => monthNames[(item.month ?? 1) - 1] ?? '');
    const data = this.ordersByMonth.map(item => item.value);
    this.chart = new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Orders',
            data,
            borderColor: primary,
            backgroundColor: 'rgba(100, 149, 237, 0.15)',
            fill: true,
            tension: 0.35,
            pointRadius: 3,
            pointBackgroundColor: primary,
            pointBorderColor: primary
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: '#6b7280' }
          },
          y: {
            beginAtZero: true,
            min: 0,
            grid: { color: '#eef0f4' },
            ticks: { color: '#6b7280', precision: 0 }
          }
        }
      }
    });
  }
}
